import { captureException } from "@sentry/react";
import { isAxiosError } from "axios";

import { Auth } from "@lib/authentication";
import { parseAuthResult } from "@lib/authentication/amplify/amplify.helpers";
import type { TechniqueSeason } from "@root/constants";

import { omgApiClient } from "./omgApiClient";

export interface UserDataResponse {
  entitlements: TechniqueSeason[];
  /**
   * Account creation date (ISO 8601) from getMe. Optional for older API
   * deployments; null when no customer row exists. There is no token fallback.
   */
  createdAt?: string | null;
}

export const MEMBER_ACCESS_DEADLINE_MS = 15_000;

// Bound token refresh as well as transport. Unknown access is an error, never
// an empty grant list. Late auth must not send a request after the deadline.
export const fetchUserData = (
  memberId: string | undefined,
): Promise<UserDataResponse> => {
  const abort = new AbortController();
  let phase = "auth";
  return new Promise<UserDataResponse>((resolve, reject) => {
    const timer = setTimeout(() => {
      phase = "timeout";
      abort.abort();
      reject(new Error("Member access deadline exceeded"));
    }, MEMBER_ACCESS_DEADLINE_MS);

    const request = async () => {
      const { accessToken } = parseAuthResult(
        await Auth.currentAuthenticatedUser(),
      );
      if (abort.signal.aborted) throw new Error("Member access expired");
      if (!memberId || !accessToken?.jwt || accessToken.sub !== memberId) {
        throw new Error("Member access authentication changed");
      }
      phase = "request";
      const response = await omgApiClient.get<UserDataResponse>(
        "/customers/me",
        {
          headers: { Authorization: `Bearer ${accessToken.jwt}` },
          timeout: MEMBER_ACCESS_DEADLINE_MS,
          signal: abort.signal,
        },
      );
      if (
        !Array.isArray(response.data?.entitlements) ||
        !response.data.entitlements.every((value) => typeof value === "string")
      ) {
        phase = "invalid_response";
        throw new Error("Invalid member access response");
      }
      return response.data;
    };

    request().then(
      (data) => {
        clearTimeout(timer);
        resolve(data);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  }).catch((error: unknown) => {
    // Never send the Axios/Auth error: it can contain tokens, headers or PII.
    const reported = new Error("Member access could not be loaded");
    reported.name = "MemberAccessLoadError";
    const status = isAxiosError(error) ? error.response?.status : undefined;
    const transportTimedOut =
      isAxiosError(error) &&
      ["ECONNABORTED", "ETIMEDOUT"].includes(error.code ?? "");
    const reason =
      phase === "request"
        ? transportTimedOut
          ? "timeout"
          : status
            ? "http"
            : "network"
        : phase;
    captureException(reported, {
      tags: {
        operation: "customers.me",
        failure_reason: reason,
        ...(status ? { http_status: String(status) } : {}),
      },
    });
    throw reported;
  });
};

// Correct the authenticated account's email after a payment-first charge
// (OMG-2094 U6). Authorized by the post-charge Cognito session; the backend
// derives the target account from the token, updates Cognito (email +
// email_verified) so the sign-in alias follows immediately with no code, and
// syncs the stored account email. Throws on an alias collision (CUSTOMER_EXISTS)
// or a malformed email (INVALID_INPUT).
export const updateCustomerEmail = async (email: string): Promise<void> => {
  const { accessToken } = parseAuthResult(
    await Auth.currentAuthenticatedUser(),
  );

  await omgApiClient.patch(
    "/customers/me/email",
    { email },
    {
      headers: {
        Authorization: `Bearer ${accessToken?.jwt}`,
      },
      // The shared client has no default timeout; without this a hung request
      // would freeze the paid buyer on the account screen (isSubmitting stuck,
      // catch/finally never run — no recovery UI). Bound it so the failure
      // surfaces (review: reliability). Mirrors the 10s post-charge sign-in cap.
      timeout: 15000,
    },
  );
};
